import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, Select, FormControl, InputLabel } from '@mui/material';
import { Add, Delete, Edit, HelpOutline } from '@mui/icons-material';
import api from '../services/api';
import type { Service, ServicePricing, ServicePricingModel } from '../types';
import { useDebounce } from '../hooks/useDebounce';
import TextField from '../components/common/TextField';

const WORKDAY_HOURS = 8;
const DEFAULT_WORKING_DAYS = 20;

const formatHoursInput = (value: number | undefined | null) => {
  const hrs = Number(value);
  if (!Number.isFinite(hrs) || hrs <= 0) return '0:00';
  const totalMinutes = Math.round(hrs * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.max(0, totalMinutes % 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const parseHoursInput = (val: string): number => {
  if (!val) return 0;
  const trimmed = val.trim().replace(',', '.');
  if (!trimmed) return 0;
  if (trimmed.includes(':')) {
    const segments = trimmed.split(':').map(seg => Number(seg));
    if (segments.some(seg => Number.isNaN(seg) || seg < 0)) return 0;
    const [hh = 0, mm = 0, ss = 0] = segments;
    return hh + (mm / 60) + (ss / 3600);
  }
  const num = Number(trimmed);
  return Number.isFinite(num) && num >= 0 ? num : 0;
};

export const ServicesPage: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);
  const [form, setForm] = useState<Partial<Service>>({ name: '', isActive: true, cost: 0, processingHoursPerUnit: 0 });
  const [pricing, setPricing] = useState<ServicePricing>({ pricingModel: 'PerUnit', unitPrice: 0, hourlyRate: 0, batchSize: null, batchPrice: null, setupCost: null });
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [hoursInput, setHoursInput] = useState('0:00');
  const [employeeCalc, setEmployeeCalc] = useState<{ salary: number; workingDays: number; hoursPerDay: number }>({
    salary: 0,
    workingDays: DEFAULT_WORKING_DAYS,
    hoursPerDay: WORKDAY_HOURS,
  });

  const hoursPerUnit = Number(form.processingHoursPerUnit) || 0;
  const unitsPerHour = hoursPerUnit > 0 ? 1 / hoursPerUnit : 0;
  const unitsPerDay = unitsPerHour > 0 ? unitsPerHour * WORKDAY_HOURS : 0;
  const employeeUnitsPerDay = hoursPerUnit > 0 ? (employeeCalc.hoursPerDay / hoursPerUnit) : 0;
  const displayEmployeeUnitsPerDay = Number.isFinite(employeeUnitsPerDay) && employeeUnitsPerDay > 0 ? employeeUnitsPerDay : 0;
  const displayUnitsPerHour = Number.isFinite(unitsPerHour) && unitsPerHour > 0 ? unitsPerHour : 0;
  const displayUnitsPerDay = Number.isFinite(unitsPerDay) && unitsPerDay > 0 ? unitsPerDay : 0;
  const employeeUnitCost = useMemo(() => {
    const salary = Number(employeeCalc.salary) || 0;
    const workingDays = Number(employeeCalc.workingDays) || 0;
    if (!salary || !workingDays || !employeeUnitsPerDay) return 0;
    if (!Number.isFinite(employeeUnitsPerDay) || employeeUnitsPerDay <= 0) return 0;
    return salary / workingDays / employeeUnitsPerDay;
  }, [employeeCalc, employeeUnitsPerDay]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getServices({ page, pageSize, search });
      setServices(res.services || []);
      setTotalPages(res.totalPages || 1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useDebounce(search, 300);
  const isFirstRender = useRef(true);

  useEffect(() => { (async () => { await load(); const s = await api.getSuppliers({ page: 1, pageSize: 500, type: 'Servicios' }); setSuppliers(s.suppliers || []); })(); }, [page, pageSize]);

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    setPage(1);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);
  useEffect(() => {
    setHoursInput(formatHoursInput(form.processingHoursPerUnit));
  }, [form.processingHoursPerUnit]);

  const onSearch = async () => { setPage(1); await load(); };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', unit: '', cost: 0, processingHoursPerUnit: 0, isActive: true });
    setPricing({ pricingModel: 'PerUnit', unitPrice: 0, hourlyRate: 0, batchSize: null, batchPrice: null, setupCost: null });
    setHoursInput('0:00');
    setEmployeeCalc({ salary: 0, workingDays: DEFAULT_WORKING_DAYS, hoursPerDay: WORKDAY_HOURS });
    setOpen(true);
  };
  const openEdit = async (s: Service) => { 
    setEditing(s); 
    const serviceHours = s.processingHoursPerUnit || 0;
    setForm({ name: s.name, code: s.code || '', unit: s.unit || '', cost: s.cost, processingHoursPerUnit: serviceHours, isActive: s.isActive }); 
    setHoursInput(formatHoursInput(serviceHours));
    try { 
      const p = await api.getServicePricing(s.id); 
      setPricing(p as any); 
      if ((p as any)?.pricingModel === 'Employee') {
        const workingDays = DEFAULT_WORKING_DAYS;
        const hoursPerDay = WORKDAY_HOURS;
        const unitsPerDayFromService = serviceHours > 0 ? hoursPerDay / serviceHours : 0;
        const salaryApprox = unitsPerDayFromService > 0 ? (Number((p as any).unitPrice || 0) * unitsPerDayFromService * workingDays) : 0;
        setEmployeeCalc({
          salary: Number(salaryApprox.toFixed(2)),
          workingDays,
          hoursPerDay,
        });
      } else {
        setEmployeeCalc({ salary: 0, workingDays: DEFAULT_WORKING_DAYS, hoursPerDay: WORKDAY_HOURS });
      }
    } catch { 
      setPricing({ pricingModel: 'PerUnit', unitPrice: s.cost, hourlyRate: s.cost } as any); 
      setEmployeeCalc({ salary: 0, workingDays: DEFAULT_WORKING_DAYS, hoursPerDay: WORKDAY_HOURS });
    }
    setOpen(true); 
  };

  const save = async () => {
    if (!form.name) return;
    const pricingPayload = pricing.pricingModel === 'Employee'
      ? {
          ...pricing,
          unitPrice: Number(employeeUnitCost.toFixed(2)),
          hourlyRate: null,
          batchSize: null,
          batchPrice: null,
        }
      : pricing;
    if (pricing.pricingModel === 'Employee' && (!employeeUnitCost || employeeUnitCost <= 0)) {
      window.alert('Completá los datos salariales y el tiempo del servicio para estimar el costo por unidad.');
      return;
    }
    if (editing) {
      const upd = await api.updateService(editing.id, form);
      try { await api.upsertServicePricing(upd.id, pricingPayload); } catch (e) { console.error(e); }
    } else {
      const created = await api.createService(form);
      try { await api.upsertServicePricing(created.id, pricingPayload); } catch (e) { console.error(e); }
    }
    setOpen(false);
    await load();
  };

  const remove = async (id: number) => { if (!window.confirm('¿Eliminar servicio?')) return; await api.deleteService(id); await load(); };

  const handleHoursPerUnitChange = (value: string) => {
    setHoursInput(value);
    const parsed = parseHoursInput(value);
    setForm({ ...form, processingHoursPerUnit: parsed });
  };

  const handleUnitsPerHourChange = (value: string) => {
    if (!value) { setForm({ ...form, processingHoursPerUnit: 0 }); return; }
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return;
    setForm({ ...form, processingHoursPerUnit: 1 / num });
  };

  const handleUnitsPerDayChange = (value: string) => {
    if (!value) { setForm({ ...form, processingHoursPerUnit: 0 }); return; }
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return;
    setForm({ ...form, processingHoursPerUnit: WORKDAY_HOURS / num });
  };

  const handlePricingModelChange = (model: ServicePricingModel) => {
    setPricing({ ...pricing, pricingModel: model });
    if (model === 'Employee') {
      const workingDays = employeeCalc.workingDays || DEFAULT_WORKING_DAYS;
      const hoursPerDay = employeeCalc.hoursPerDay || WORKDAY_HOURS;
      const unitsPerDayFromService = hoursPerUnit > 0 ? hoursPerDay / hoursPerUnit : 0;
      const salaryApprox = unitsPerDayFromService > 0 ? (Number(pricing.unitPrice || 0) * unitsPerDayFromService * workingDays) : employeeCalc.salary;
      setEmployeeCalc({
        salary: Number((salaryApprox || 0).toFixed(2)),
        workingDays,
        hoursPerDay,
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Servicios</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Nuevo servicio</Button>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField size="small" placeholder="Buscar por nombre o código" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onSearch()} />
        <Button variant="outlined" onClick={onSearch}>Buscar</Button>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Código</TableCell>
              <TableCell>Unidad</TableCell>
              <TableCell align="right">Costo unit.</TableCell>
              <TableCell align="right">Horas por unidad</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services.map((s) => (
              <TableRow key={s.id} hover>
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.code}</TableCell>
                <TableCell>{s.unit}</TableCell>
                <TableCell align="right">{Number(s.cost || 0).toFixed(2)}</TableCell>
                <TableCell align="right">{formatHoursInput(s.processingHoursPerUnit)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(s)}><Edit /></IconButton></Tooltip>
                  <Tooltip title="Eliminar"><IconButton size="small" onClick={() => remove(s.id)} color="error"><Delete /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {services.length === 0 && !loading && (
              <TableRow><TableCell colSpan={6} align="center"><Typography color="text.secondary">Sin resultados</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} />
      </Stack>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar servicio' : 'Nuevo servicio'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Nombre"
                value={form.name || ''}
                onChange={e => setForm({ ...form, name: e.target.value })}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Nombre del servicio tal como lo vas a ver en formulaciones y reportes. Ej.: 'Encapsulado creatina', 'Envasado sachets', 'Mezcla y envasado'.">
                      <IconButton size="small" edge="end">
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              {editing && (
                <TextField
                  label="Código"
                  value={form.code || ''}
                  onChange={e => setForm({ ...form, code: e.target.value })}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <Tooltip title="Código interno u hoja de ruta para este servicio. Opcional, pero útil si tenés códigos en tu ERP.">
                        <IconButton size="small" edge="end">
                          <HelpOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
              )}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Unidad"
                value={form.unit || ''}
                onChange={e => setForm({ ...form, unit: e.target.value })}
                fullWidth
                helperText="Cómo medís una unidad de este servicio"
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Unidad en la que se mide el servicio. Ej.: 'u' (unidad producida), 'frasco', 'kg procesado', 'lote'. Esta unidad se usa en formulaciones y en el modelo de precios.">
                      <IconButton size="small" edge="end">
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              <TextField
                type="number"
                label="Costo base (fallback)"
                value={form.cost ?? 0}
                onChange={e => setForm({ ...form, cost: Number(e.target.value) })}
                helperText="Usado si no hay pricing detallado"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Costo por 1 unidad de servicio cuando no tenés un modelo de precio configurado o falta información. Ej.: 0,50 USD por frasco llenado.">
                      <IconButton size="small" edge="end">
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Horas por unidad"
                value={hoursInput}
                onChange={e => handleHoursPerUnitChange(e.target.value)}
                helperText="Ingresá en formato decimal o HH:MM (ej.: 1:30)"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Cuánto tiempo lleva producir 1 unidad. Podés escribir 1.5 (1 hora y media) o 1:30. Se usa para capacidad y para calcular costos cuando el precio es por hora o empleado.">
                      <IconButton size="small" edge="end">
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              <TextField
                type="number"
                label="Unidades por hora (por persona)"
                value={displayUnitsPerHour ? Number(displayUnitsPerHour.toFixed(2)) : ''}
                onChange={e => handleUnitsPerHourChange(e.target.value)}
                helperText="Alternativa: cuántas unidades produce en 1 hora"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Otra forma de cargar la productividad: cuántas unidades produce una persona en 1 hora. Ej.: si puede hacer 40 unidades/hora, la app lo convierte a horas por unidad.">
                      <IconButton size="small" edge="end">
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              <TextField
                type="number"
                label={`Unidades por turno (${WORKDAY_HOURS}h)`}
                value={displayUnitsPerDay ? Number(displayUnitsPerDay.toFixed(1)) : ''}
                onChange={e => handleUnitsPerDayChange(e.target.value)}
                helperText="Cuántas unidades produce una persona en un turno completo"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Tooltip title={`Si un turno son ${WORKDAY_HOURS} horas, indicá cuántas unidades puede producir una persona en ese turno. Ej.: 320 unidades en 8h. También se convierte automáticamente a horas por unidad.`}>
                      <IconButton size="small" edge="end">
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
            </Stack>

            {/* Pricing section */}
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" fontWeight={600}>Modo de precio (cuanto nos cobran por el servicio)</Typography>
                <Tooltip title="Define cómo te cobra este servicio el proveedor o el empleado interno. Por unidad (cada unidad producida), por lote (un precio por X unidades), por hora o como empleado interno (salario mensual prorrateado).">
                  <IconButton size="small">
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Modelo</InputLabel>
                  <Select label="Modelo" value={pricing.pricingModel as any} onChange={e => handlePricingModelChange(e.target.value as ServicePricingModel)}>
                    <MenuItem value="PerUnit">Por unidad</MenuItem>
                    <MenuItem value="PerBatch">Por lote</MenuItem>
                    <MenuItem value="PerHour">Por hora</MenuItem>
                    <MenuItem value="Employee">Empleado interno</MenuItem>
                  </Select>
                </FormControl>
                {pricing.pricingModel === 'PerUnit' && (
                  <TextField
                    type="number"
                    label="Precio unitario"
                    value={pricing.unitPrice ?? 0}
                    onChange={e => setPricing({ ...pricing, unitPrice: Number(e.target.value) })}
                    fullWidth
                    helperText="Cuánto te cobra el proveedor por 1 unidad del servicio"
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Precio por 1 unidad de servicio. Ej.: si un tercero te cobra 0,40 USD por cada frasco envasado, poné 0,40 acá.">
                          <IconButton size="small" edge="end">
                            <HelpOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                )}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Proveedor (servicio)</InputLabel>
                  <Select label="Proveedor (servicio)" value={pricing.supplierId ?? ''} onChange={e => setPricing({ ...pricing, supplierId: e.target.value ? Number(e.target.value) : null })}>
                    <MenuItem value="">Sin asignar</MenuItem>
                    {suppliers.map(s => <MenuItem key={s.id} value={s.id}>{s.name}{s.company ? ` — ${s.company}` : ''}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
              {pricing.pricingModel === 'PerBatch' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    type="number"
                    label="Tamaño lote"
                    value={pricing.batchSize ?? ''}
                    onChange={e => setPricing({ ...pricing, batchSize: e.target.value ? Number(e.target.value) : null })}
                    fullWidth
                    helperText="Cuántas unidades incluye un lote cuando te cobran por lote"
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Número de unidades que incluye un lote. Ej.: te cobran un precio fijo por cada lote de 1.000 unidades envasadas, entonces el tamaño de lote es 1000.">
                          <IconButton size="small" edge="end">
                            <HelpOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                  <TextField
                    type="number"
                    label="Precio por lote"
                    value={pricing.batchPrice ?? ''}
                    onChange={e => setPricing({ ...pricing, batchPrice: e.target.value ? Number(e.target.value) : null })}
                    fullWidth
                    helperText="Cuánto te cobran por cada lote completo"
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Precio total por lote. Ej.: 400 USD por lote de 1.000 unidades, lo que se prorratea en 0,40 por unidad.">
                          <IconButton size="small" edge="end">
                            <HelpOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                  <TextField
                    type="number"
                    label="Setup (opcional)"
                    value={pricing.setupCost ?? ''}
                    onChange={e => setPricing({ ...pricing, setupCost: e.target.value ? Number(e.target.value) : null })}
                    fullWidth
                    helperText="Costo fijo de preparación/puesta en marcha"
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Costo fijo de preparar la máquina o línea antes de producir (cambio de formato, limpieza, etc.). Se reparte entre todas las unidades del lote.">
                          <IconButton size="small" edge="end">
                            <HelpOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                </Stack>
              )}
              {pricing.pricingModel === 'Employee' && (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      type="number"
                      label="Salario mensual del empleado"
                      value={employeeCalc.salary || ''}
                      onChange={e => setEmployeeCalc({ ...employeeCalc, salary: Number(e.target.value) || 0 })}
                      helperText="Costo total del empleado (salario + cargas) por mes"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="Costo mensual completo del empleado que hace este servicio: salario bruto + cargas + otros costos. Se prorratea según cuántas unidades produce.">
                            <IconButton size="small" edge="end">
                              <HelpOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ),
                      }}
                    />
                    <TextField
                      type="number"
                      label="Días laborales / mes"
                      value={employeeCalc.workingDays}
                      onChange={e => setEmployeeCalc({ ...employeeCalc, workingDays: Number(e.target.value) || DEFAULT_WORKING_DAYS })}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="Cantidad de días que realmente trabaja al mes (descontando francos, feriados, etc.). Ej.: 20 días hábiles.">
                            <IconButton size="small" edge="end">
                              <HelpOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ),
                      }}
                    />
                    <TextField
                      type="number"
                      label="Horas trabajadas por día"
                      value={employeeCalc.hoursPerDay}
                      onChange={e => setEmployeeCalc({ ...employeeCalc, hoursPerDay: Number(e.target.value) || WORKDAY_HOURS })}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="Horas efectivas de trabajo por día. Ej.: 8 horas de trabajo productivo por turno.">
                            <IconButton size="small" edge="end">
                              <HelpOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ),
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Unidades por día (según productividad): <strong>{displayEmployeeUnitsPerDay ? displayEmployeeUnitsPerDay.toFixed(1) : '—'}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Costo estimado por unidad (prorrateado): <strong>{employeeUnitCost > 0 ? `$${employeeUnitCost.toFixed(2)}` : '—'}</strong>
                  </Typography>
                </Stack>
              )}
              {pricing.pricingModel === 'PerHour' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    type="number"
                    label="Tarifa por hora"
                    value={pricing.hourlyRate ?? 0}
                    onChange={e => setPricing({ ...pricing, hourlyRate: Number(e.target.value) })}
                    fullWidth
                    helperText="Cuánto te cobran por cada hora de servicio"
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Tarifa que cobra el proveedor por cada hora de servicio. Ej.: 50 USD por hora de envasado. Se combina con las horas por unidad para estimar el costo unitario.">
                          <IconButton size="small" edge="end">
                            <HelpOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                  <TextField
                    type="number"
                    label="Setup (opcional)"
                    value={pricing.setupCost ?? ''}
                    onChange={e => setPricing({ ...pricing, setupCost: e.target.value ? Number(e.target.value) : null })}
                    fullWidth
                    helperText="Costo fijo de preparación/puesta en marcha"
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Costo fijo adicional por preparar la línea para el servicio (montaje, limpieza, calibración). Se reparte entre las unidades producidas.">
                          <IconButton size="small" edge="end">
                            <HelpOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                </Stack>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  type="number"
                  label="Cuánto tarda (días, por defecto)"
                  value={pricing.defaultLeadDays ?? ''}
                  onChange={e => setPricing({ ...pricing, defaultLeadDays: e.target.value ? Number(e.target.value) : null })}
                  fullWidth
                  helperText="Tiempo típico que tarda el servicio desde que lo pedís"
                  InputProps={{
                    endAdornment: (
                      <Tooltip title="Lead time estándar del servicio en días: desde que lo pedís hasta que está terminado. Ej.: 5 días para un servicio de envasado externo.">
                        <IconButton size="small" edge="end">
                          <HelpOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
                <TextField
                  type="number"
                  label="Mínimo por defecto"
                  value={pricing.defaultMinOrderQty ?? ''}
                  onChange={e => setPricing({ ...pricing, defaultMinOrderQty: e.target.value ? Number(e.target.value) : null })}
                  fullWidth
                  helperText="Cantidad mínima típica para solicitar el servicio"
                  InputProps={{
                    endAdornment: (
                      <Tooltip title="Cantidad mínima de unidades que normalmente te exige el proveedor o que vos considerás como lote mínimo razonable. Ej.: mínimo 500 unidades por orden.">
                        <IconButton size="small" edge="end">
                          <HelpOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save}>{editing ? 'Guardar' : 'Crear'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServicesPage;
